import {
  DEFAULT_MODE,
  DEFAULT_PALETTE_ID,
  getButtonPalette,
  getPalette,
  type Mode,
} from "@/themes/palettes"

const STORAGE_KEY = "ait-theme"

export type ThemeSelection = {
  paletteId: string
  mode: Mode
  // null/"" => use the active theme's native accent (no override)
  buttonsId: string | null
}

// camelCase token key -> CSS custom property name (primaryForeground -> --primary-foreground).
function cssVarName(key: string) {
  return "--" + key.replace(/[A-Z]/g, (c) => "-" + c.toLowerCase())
}

function writeVars(vars: Record<string, string>) {
  const root = document.documentElement
  for (const [key, value] of Object.entries(vars)) {
    root.style.setProperty(cssVarName(key), value)
  }
}

/** Resolve a selection to the palette's actual mode, falling back if a mode is absent. */
export function resolveMode(paletteId: string, mode: Mode): Mode {
  const palette = getPalette(paletteId)
  if (palette.modes[mode]) return mode
  return palette.modes.light ? "light" : "dark"
}

export function applyPalette({ paletteId, mode, buttonsId }: ThemeSelection) {
  const palette = getPalette(paletteId)
  const resolved = resolveMode(paletteId, mode)
  const tokens = palette.modes[resolved]
  if (!tokens) return

  writeVars(tokens)

  // Second axis: overlay the action/button palette's CTA tokens, if one is chosen.
  const buttons = getButtonPalette(buttonsId)
  if (buttons) writeVars(buttons.tokens)

  const root = document.documentElement
  root.setAttribute("data-palette", palette.id)
  root.setAttribute("data-mode", resolved)
  root.setAttribute("data-buttons", buttons?.id ?? "")
  root.style.colorScheme = resolved
}

export function loadSelection(): ThemeSelection {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ThemeSelection>
      if (parsed.paletteId && parsed.mode) {
        return {
          paletteId: parsed.paletteId,
          mode: parsed.mode,
          buttonsId: parsed.buttonsId ?? null,
        }
      }
    }
  } catch {
    // ignore malformed storage
  }
  return { paletteId: DEFAULT_PALETTE_ID, mode: DEFAULT_MODE, buttonsId: null }
}

export function saveSelection(selection: ThemeSelection) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(selection))
}
